// Kit 03 · items 76 to 87. "Absence states what is absent."
//
//   76/77/78 empty       icon 24 · gap 12 · pad 32 · copy measure 44ch
//   79/86/87 unmeasured  row gap 12 · dot 6 · mono meta 11
//   80 to 84 error       icon 24 · gap 12 · pad 32 · inline icon 14
//
// The whole section is one rule seen from five angles: never a zero for an
// absence. No 0, no dash, no N/A, no empty cell. A state that has nothing to
// show says what is missing and what would produce it.

import type { ReactNode } from "react";
import { CircleAlert, Lock, RotateCw } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./icon";
import { Button } from "./button";
import { NOT_MEASURED } from "./form";
import { cx } from "./kit-state";

/* ═══ 76, 77, 78 · empty ════════════════════════════════════════════════════
   keys: "The primary action is the first tab stop inside the region."

   Which is why the action is a prop rendered last in the DOM but is still the
   first focusable: nothing above it in the region is focusable at all. An
   empty state with a link in its description would break that quietly, so the
   description takes a string.
   ═════════════════════════════════════════════════════════════════════════ */

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  /** One or two sentences saying what would produce the missing thing.
      Measured at 44ch, which is the specimen's number and roughly one clause
      per line at --t-small. */
  description: string;
  /** The thing to do about it. Optional: an empty table has nothing to offer. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cx("k-empty", className)} role="status">
      <span className="k-empty__mark"><Icon icon={icon} size="xl" /></span>
      <span className="k-empty__title">{title}</span>
      <p className="k-empty__copy">{description}</p>
      {action}
    </div>
  );
}

/**
 * 77 · an empty table, 78 · an empty search.
 *
 * A quieter box than 76 because it sits inside a container that is not itself
 * empty: the table has headers, the search has a query. It states the absence
 * and nothing else.
 *
 * `query` is the searched term. Passing it renders 78's form; omitting it
 * renders 77's. One component, because the two differ by one clause.
 */
export function EmptyRow({
  children, className,
}: { children: ReactNode; className?: string }) {
  return <div className={cx("k-empty-row", className)} role="status">{children}</div>;
}

/* ═══ 79 · unmeasured ═══════════════════════════════════════════════════════
   "Never a zero, never a dash, never an empty cell."

   The card form of what form.tsx's Readout does inline. Both render the same
   string from the same constant, so the sixteen canonical strings stay
   canonical.
   ═════════════════════════════════════════════════════════════════════════ */

export function Unmeasured({
  label, note, className,
}: { label: string; note?: ReactNode; className?: string }) {
  return (
    <div className={cx("k-unmeasured", className)}>
      <span className="k-meta">{label}</span>
      <span className="k-unmeasured__value">{NOT_MEASURED}</span>
      {note ? <span className="k-unmeasured__note">{note}</span> : null}
    </div>
  );
}

/* ═══ 86 · freshness ════════════════════════════════════════════════════════
   redline: dot 6 · mono meta 11
   keys: "Not focusable. The freshness line is read with the value it
   qualifies, never separately."

   Two things are derived here rather than passed, because passing either is
   how a caption comes to disagree with the thing it captions:
   the "N days ago" clause, and the colour of the dot.
   ═════════════════════════════════════════════════════════════════════════ */

const DAY_MS = 24 * 60 * 60 * 1000;

export interface FreshnessProps {
  /** When the reading was taken. */
  measuredAt: Date;
  /** Now. A prop so a test and a screenshot can both be deterministic. */
  now?: Date;
  /**
   * How old a reading may be before it needs re-measuring.
   *
   * REQUIRED, and deliberately without a default. No kit states a staleness
   * threshold: the specimen draws 2 days green and 34 days amber and says
   * nothing about where the line is between them. That number belongs to the
   * measurement cadence, which is the caller's knowledge and not a design
   * value, so this component will not guess it. design/TICKETS.md UPM-T12.
   */
  staleAfterDays: number;
  /** How the date reads. Formatted by the caller so one locale decision is
      made in one place rather than per component. */
  formatted: string;
  className?: string;
}

export function Freshness({ measuredAt, now = new Date(), staleAfterDays, formatted, className }: FreshnessProps) {
  const days = Math.floor((now.getTime() - measuredAt.getTime()) / DAY_MS);
  const stale = days >= staleAfterDays;
  return (
    <span className={cx("k-fresh", className)} data-tone={stale ? "warn" : "ok"}>
      <span className="k-fresh__dot" aria-hidden="true" />
      <span className="k-meta">
        measured {formatted}, {days} {days === 1 ? "day" : "days"} ago
      </span>
    </span>
  );
}

/* ═══ 87 · cap reached ══════════════════════════════════════════════════════
   A limit stated with the way out of it. "Remove one to add another" is the
   half that makes it a notice rather than a refusal.
   ═════════════════════════════════════════════════════════════════════════ */

export function CapNotice({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("k-cap", className)} role="status">{children}</div>;
}

/* ═══ 80, 81 · error region ═════════════════════════════════════════════════
   keys: "The retry control is the first tab stop."

   80 is the page that did not load and 81 is the section that did not; the
   specimen draws one region and covers both numbers with it, so there is one
   component. What changes between them is the sentence, not the form.
   ═════════════════════════════════════════════════════════════════════════ */

export interface ErrorStateProps {
  title: string;
  /** What was NOT lost. Item 80's second line is the whole reason the state
      reads as recoverable: "Nothing was lost. Your last measurement is
      unchanged." */
  description: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

export function ErrorState({
  title, description, onRetry, retryLabel = "Try again", className,
}: ErrorStateProps) {
  return (
    <div className={cx("k-error", className)} role="alert">
      <span className="k-error__mark"><Icon icon={CircleAlert} size="xl" /></span>
      <span className="k-error__title">{title}</span>
      <p className="k-error__copy">{description}</p>
      {onRetry ? (
        <Button variant="secondary" icon={RotateCw} onClick={onRetry}>{retryLabel}</Button>
      ) : null}
    </div>
  );
}

/* ═══ 82 · inline error ═════════════════════════════════════════════════════
   keys: "An inline error is announced with the field it belongs to."

   So there is no role="alert" here and no live region: the field points at
   this node with aria-describedby and aria-invalid, and the field's own
   announcement carries it. A second live region would say it twice.
   ═════════════════════════════════════════════════════════════════════════ */

export function InlineError({
  children, id, className,
}: { children: ReactNode; id?: string; className?: string }) {
  return (
    <span className={cx("k-inline-err", className)} id={id}>
      <Icon icon={CircleAlert} size="sm" />
      {children}
    </span>
  );
}

/* ═══ 83, 84 · locked ═══════════════════════════════════════════════════════
   "A locked surface warms rather than dimming further, so it reads as
   something you can buy rather than something that is broken."

   --a-brand-sub, not --i-dis-bg. The same decision the nav rail's locked rows
   made in Phase 1, for the same reason, and one class so the two cannot drift.
   ═════════════════════════════════════════════════════════════════════════ */

export function LockedNotice({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("k-locked", className)}>
      <span className="k-locked__mark"><Icon icon={Lock} size="sm" /></span>
      <span className="k-locked__text">{children}</span>
    </div>
  );
}

export { NOT_MEASURED };
